import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Checkbox, DatePicker, Input, SearchableSelect, Select, PhoneInput } from "@/shared/ui";
import { getFamilyStatusOptions, type Locale } from "@/shared/utils";
import type { FieldAvailabilityState } from "@/shared/hooks";
import { getNationalityOptions } from "@/features/hr/hiring/consts/nationality";
import { CITIZENSHIP_OPTIONS } from "@/features/hr/hiring/consts/citizenship";
import type { ImportWorkerFormValues } from "../types";
import { GENDER_OPTIONS } from "../consts";

interface Props {
  availability?: {
    iin: FieldAvailabilityState;
    email: FieldAvailabilityState;
    phone: FieldAvailabilityState;
  };
}

export default function PersonalSection({ availability }: Props) {
  const { t, i18n } = useTranslation("ImportWorkerPage");
  const { t: tCommon } = useTranslation("Common");
  const { t: tApply } = useTranslation("ApplyApplicationPage");
  const locale = i18n.language as Locale;

  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ImportWorkerFormValues>();

  const iinValue = watch("iin");
  const dateOfBirth = watch("date_of_birth");
  const gender = watch("gender");
  const familyStatus = watch("family_status");
  const nationality = watch("nationality");
  const citizenship = watch("citizenship");
  const isResident = watch("is_resident");
  const isStudent = watch("is_student");
  const enrollmentStartDate = watch("enrollment_start_date");
  const enrollmentEndDate = watch("enrollment_end_date");

  useEffect(function parseIinData() {
    if (!iinValue || iinValue.length < 7 || !/^\d{7}/.test(iinValue)) return;

    const yy = parseInt(iinValue.substring(0, 2), 10);
    const mm = parseInt(iinValue.substring(2, 4), 10);
    const dd = parseInt(iinValue.substring(4, 6), 10);
    const centuryDigit = parseInt(iinValue[6], 10);

    let century: number;
    if (centuryDigit === 1 || centuryDigit === 2) century = 1800;
    else if (centuryDigit === 3 || centuryDigit === 4) century = 1900;
    else if (centuryDigit === 5 || centuryDigit === 6) century = 2000;
    else return;

    const year = century + yy;
    const date = new Date(year, mm - 1, dd);

    if (date.getFullYear() === year && date.getMonth() === mm - 1 && date.getDate() === dd) {
      setValue("date_of_birth", date, { shouldValidate: true });
    }

    const parsedGender = centuryDigit % 2 === 1 ? "male" : "female";
    setValue("gender", parsedGender, { shouldValidate: true });
  }, [iinValue, setValue]);

  const nationalityOptions = getNationalityOptions(gender, tApply);
  const nationalityOption = nationalityOptions.find((o) => o.value === nationality) ?? null;
  const citizenshipOption = CITIZENSHIP_OPTIONS.find((o) => o.value === citizenship) ?? null;

  const genderOptions = GENDER_OPTIONS.map((o) => ({
    label: t(o.labelKey),
    value: o.value,
  }));

  const familyStatusOptions = getFamilyStatusOptions(gender, tCommon);

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-display-2xs content-base-primary">{t("sections.personal")}</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 items-start gap-4">
        <Input
          label={t("personal.iin")}
          placeholder={t("personal.iinPlaceholder")}
          maxLength={12}
          hasRequiredLabel
          {...register("iin")}
          error={
            errors.iin?.message
              ? t(errors.iin.message)
              : availability?.iin === "taken"
                ? t("uniqueness.takenByField.iin")
                : undefined
          }
        />

        <DatePicker
          label={t("personal.dateOfBirth")}
          placeholder={t("personal.dateOfBirthPlaceholder")}
          locale={locale}
          value={dateOfBirth}
          hasRequiredLabel
          onChange={(v) => setValue("date_of_birth", v as Date | null, { shouldValidate: true })}
          error={errors.date_of_birth?.message ? t(errors.date_of_birth.message) : undefined}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 items-end gap-4">
        <Input
          label={t("personal.surname")}
          placeholder={t("personal.surnamePlaceholder")}
          hasRequiredLabel
          {...register("surname")}
          error={errors.surname?.message ? t(errors.surname.message) : undefined}
        />

        <Input
          label={t("personal.name")}
          placeholder={t("personal.namePlaceholder")}
          hasRequiredLabel
          {...register("name")}
          error={errors.name?.message ? t(errors.name.message) : undefined}
        />

        <Input
          label={t("personal.fatherName")}
          placeholder={t("personal.fatherNamePlaceholder")}
          {...register("father_name")}
          error={errors.father_name?.message ? t(errors.father_name.message) : undefined}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-4">
        <Select
          label={t("personal.gender")}
          placeholder={t("personal.genderPlaceholder")}
          options={genderOptions}
          value={gender || null}
          hasRequiredLabel
          onChange={(v) => {
            const newGender = (v ?? "") as ImportWorkerFormValues["gender"];
            setValue("gender", newGender, { shouldValidate: true });
            if (newGender !== "male") {
              setValue("military_certificate_file", null);
            }
          }}
          error={errors.gender?.message ? t(errors.gender.message) : undefined}
        />

        <Select
          label={t("personal.familyStatus")}
          placeholder={t("personal.familyStatusPlaceholder")}
          options={familyStatusOptions}
          value={familyStatus || null}
          onChange={(v) => setValue("family_status", (v ?? "") as ImportWorkerFormValues["family_status"], { shouldValidate: true })}
          error={errors.family_status?.message ? t(errors.family_status.message) : undefined}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-4">
        <SearchableSelect
          label={t("personal.nationality")}
          placeholder={t("personal.nationalityPlaceholder")}
          options={nationalityOptions}
          searchKeys={["label", "value"]}
          displayKey="label"
          value={nationalityOption}
          hasRequiredLabel
          onChange={(o) => setValue("nationality", o?.value ?? "", { shouldValidate: true })}
          error={errors.nationality?.message ? t(errors.nationality.message) : undefined}
        />

        <SearchableSelect
          label={t("personal.citizenship")}
          placeholder={t("personal.citizenshipPlaceholder")}
          options={CITIZENSHIP_OPTIONS}
          searchKeys={["value", "labelKey"]}
          displayKey="value"
          value={citizenshipOption}
          onChange={(o) => setValue("citizenship", o?.value ?? "", { shouldValidate: true })}
          error={errors.citizenship?.message ? t(errors.citizenship.message) : undefined}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 items-start gap-4">
        <Input
          label={t("personal.email")}
          placeholder={t("personal.emailPlaceholder")}
          type="email"
          hasRequiredLabel
          {...register("email")}
          error={
            errors.email?.message
              ? t(errors.email.message)
              : availability?.email === "taken"
                ? t("uniqueness.takenByField.email")
                : undefined
          }
        />

        <PhoneInput
          label={t("personal.phone")}
          hasRequiredLabel
          value={watch("phone")}
          onChange={(value) => setValue("phone", value, { shouldDirty: true })}
          error={
            errors.phone?.message
              ? t(errors.phone.message)
              : availability?.phone === "taken"
                ? t("uniqueness.takenByField.phone")
                : undefined
          }
        />
      </div>

      <div className="flex flex-col gap-2">
        <Checkbox
          label={t("personal.notResident")}
          state={isResident ? "unchecked" : "checked"}
          onChange={() => setValue("is_resident", !isResident, { shouldValidate: true })}
        />

        <Checkbox
          label={t("personal.isStudent")}
          state={isStudent ? "checked" : "unchecked"}
          onChange={() => {
            const next = !isStudent;
            setValue("is_student", next, { shouldValidate: true });
            if (!next) {
              setValue("enrollment_verification_file", null);
              setValue("enrollment_start_date", null, { shouldValidate: true });
              setValue("enrollment_end_date", null, { shouldValidate: true });
            }
          }}
        />
      </div>

      {isStudent && (
        <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-4">
          <DatePicker
            label={t("personal.enrollmentStartDate")}
            placeholder={t("personal.enrollmentStartDatePlaceholder")}
            locale={locale}
            value={enrollmentStartDate}
            hasRequiredLabel
            onChange={(v) => setValue("enrollment_start_date", v as Date | null, { shouldValidate: true })}
            error={errors.enrollment_start_date?.message ? t(errors.enrollment_start_date.message) : undefined}
          />

          <DatePicker
            label={t("personal.enrollmentEndDate")}
            placeholder={t("personal.enrollmentEndDatePlaceholder")}
            locale={locale}
            value={enrollmentEndDate}
            hasRequiredLabel
            onChange={(v) => setValue("enrollment_end_date", v as Date | null, { shouldValidate: true })}
            error={errors.enrollment_end_date?.message ? t(errors.enrollment_end_date.message) : undefined}
          />
        </div>
      )}
    </div>
  );
}
